import { Injectable } from '@angular/core';
import { CPI } from '../../../shared/interfaces/cpi.interface';
import {
  MultiCountryDraftData,
  Multicountry,
} from '../../../shared/interfaces/create-surveys.interface';
import {
  SupplierResponseInterface,
  GetSupplierBlendInterface,
  PutSupplierObjectInterface,
  SupplierQuantity,
} from '../../../shared/interfaces/survey.interface';
import { BuyerApiService } from '../../../shared/services/buyer-api/survey.service';
import { FeasibilityResponse } from '../../../shared/interfaces/feasibility.interface';
import { GetBuyerData } from '../../../shared/interfaces/survey-meta-data.interface';
import { Subject } from 'rxjs';
import { AllocationFactory, Flexibility } from './../domain/allocation';
import { Suppliers } from '../../../shared/domain/suppliers';

@Injectable({
  providedIn: 'root',
})
export class CpiService {
  // cached props for later requests
  private _suppliers: SupplierResponseInterface[] = [];
  private _buyerData?: GetBuyerData;

  private _cpiVisibility = new Subject<boolean>();
  public cpiVisibility = this._cpiVisibility.asObservable();

  constructor(private _buyerApiService: BuyerApiService) {}

  updateCpiVisibility(state: boolean) {
    this._cpiVisibility.next(state);
  }

  mapCpiWithoutFeasibility(suppliers: SupplierResponseInterface[]) {
    const cpis: CPI[] = [];
    suppliers.forEach((supplier) => {
      const {
        cpi,
        block_price,
        suppliers: [{ id, name }],
        quantity,
        group_name,
        is_active,
        scpi,
        is_pds_applied,
        fee,
      } = supplier;
      let cpiNumber: number = typeof cpi === 'number' ? cpi : Number(cpi || 0);
      if (!cpiNumber && block_price) {
        cpiNumber = block_price;
      }

      const cpiFeasibility: number = supplier.suppliers.reduce(
        (prev, item) => prev + (item.feasibility || 0),
        0
      );

      const unusedQualifications: string = '';

      const isSelected = !!(is_active && quantity > 0);

      cpis.push({
        id,
        cpi: cpiNumber,
        supplier: group_name || name,
        selected: isSelected,
        feasibility: 1,
        isActive: is_active,
        unusedQualifications,
        scpi,
        isPdsApplied: is_pds_applied,
        fee,
      });
    });
    return cpis;
  }

  mapCpi(
    suppliers: SupplierResponseInterface[],
    buyerData: GetBuyerData,
    feasibility: FeasibilityResponse,
    isActiveSurvey: boolean = true
  ): CPI[] {
    // Only for currently active survey, we should be updating _suppliers array and _buyerData
    if (isActiveSurvey && buyerData) {
      this._suppliers = suppliers;
      this._buyerData = buyerData;
    }
    const cpis: CPI[] = [];
    suppliers.forEach((supplier) => {
      const {
        cpi,
        block_price,
        suppliers: [{ id, name }],
        quantity,
        group_name,
        is_active,
        scpi,
        is_pds_applied,
        fee,
      } = supplier;
      let cpiNumber: number = typeof cpi === 'number' ? cpi : Number(cpi || 0);
      if (!cpiNumber && block_price) {
        cpiNumber = block_price;
      }

      const cpiFeasibility: number = supplier.suppliers.reduce(
        (prev, item) => prev + (item.feasibility || 0),
        0
      );

      const unusedQualifications: string = this.mapUnusedQualifications(
        feasibility.unused_qualifications || []
      ).join(',');

      const isSelected = !!(is_active && quantity > 0);

      cpis.push({
        id,
        cpi: cpiNumber,
        supplier: group_name || name,
        selected: isSelected,
        feasibility: cpiFeasibility,
        isActive: is_active,
        unusedQualifications,
        scpi,
        isPdsApplied: is_pds_applied,
        fee,
      });
    });
    return cpis;
  }

  public mapUnusedQualifications(unUsedQualifications: number[]) {
    const qualifications = this._buyerData?.surveyQualifications;
    let unUsedQualificationNames: string[] = [];
    if (unUsedQualifications && unUsedQualifications.length) {
      unUsedQualifications.forEach((qualificationCode) => {
        const qualificationObj = qualifications?.find(
          (qualification) =>
            qualification.qualification_code === qualificationCode
        );
        if (qualificationObj) {
          unUsedQualificationNames.push(qualificationObj.qualification_name);
        }
      });
    }
    return unUsedQualificationNames;
  }

  getTotalCpi(cpi: CPI[], response = <FeasibilityResponse>{}) {
    const selectedCpis = cpi.filter((c) => c.selected);
    const totalCpiFeasibility = response.total_feasibility;
    // If there are no feasibility numbers in a survey, then calculating CPI based
    // on averaging out the supplier CPIs.
    if (!totalCpiFeasibility) {
      const totalCpi = selectedCpis.reduce(
        (total: number, c) => total + c.cpi,
        0
      );
      const validCpis = selectedCpis.reduce(
        (total: number, c) => total + (c.cpi && 1),
        0
      );
      if (!totalCpi) {
        return 0;
      }
      return totalCpi / validCpis;
    }
    // If feasibility numbers are there, then using feasibility based CPI as total CPI
    return response.feasibility_cpi || 0;
  }

  getAverageCpi(multiCountryDraftData: MultiCountryDraftData) {
    const { totalCount, totalCpi } = multiCountryDraftData.countryData.reduce(
      (
        cpiObj: { totalCount: number; totalCpi: number },
        countryData: Multicountry
      ) => {
        if (countryData.checked) {
          cpiObj.totalCpi += Number(countryData.cpi) || 0;
          cpiObj.totalCount += 1;
        }
        return cpiObj;
      },
      { totalCount: 0, totalCpi: 0 }
    );
    return totalCount ? totalCpi / totalCount : 0;
  }

  async setSuppliers(
    cpi: CPI[],
    surveyPriceOverride: number = 0,
    manualOverride: boolean = false,
    surveyPauseThreshold: number = 0,
    completesRequired: number = 0,
    isServiceBuyerLoggedIn: boolean = false,
    manualSCPIOverrideEnabled: boolean = false,
    supplierBlendData: GetSupplierBlendInterface[] = [],
    selectedBlendId: string = '',
    isPurePriceRateCardEnable?: boolean,
    suppliersList?: SupplierResponseInterface[],
    isAugmentSurvey: boolean = false
  ) {
    this._suppliers = suppliersList ? suppliersList : this._suppliers;

    let suppliers = this.mapSuppliers(
      cpi,
      completesRequired,
      isServiceBuyerLoggedIn,
      supplierBlendData,
      selectedBlendId,
      isAugmentSurvey
    );
    suppliers = suppliers.filter(
      (sup, index) =>
        index ===
        suppliers.findIndex((otherSup) =>
          sup.suppliers.includes(otherSup.suppliers[0])
        )
    );
    if (isServiceBuyerLoggedIn && surveyPriceOverride) {
      suppliers.forEach((supplier) => {
        supplier.cpi = Number(surveyPriceOverride);
      });
    }
    this._suppliers = await this._buyerApiService
      .putSuppliers(
        this._buyerData!.surveyId,
        'editable',
        suppliers,
        surveyPriceOverride,
        manualOverride,
        surveyPauseThreshold,
        this._buyerData!.expected_loi,
        this._buyerData!.expected_ir,
        this._buyerData!.language,
        selectedBlendId,
        manualSCPIOverrideEnabled,
        isPurePriceRateCardEnable,
        this._buyerData!.field_time
      )
      .toPromise();
    // Assign each supplier isChecked based on whether it is selected or not
    const selectedCpis = cpi.filter((c) => c.selected);
    return this._suppliers.map((s) => {
      s.isChecked = selectedCpis.some((c) => c.id === s.suppliers[0].id);
      return s;
    });
  }

  getQuantityFromSuppliers(id: number, supplier: SupplierResponseInterface[]) {
    return supplier.find((supp) => supp.id == id)?.quantity || 0;
  }

  theresSupplierWithoutQuantity(suppliers: CPI[]) {
    return suppliers.length !== 0;
  }

  sumOfSuppliersQuantity(suppliers: SupplierResponseInterface[]) {
    return suppliers.reduce((total, supplier) => supplier.quantity + total, 0);
  }

  uncheckedSuppliers(
    suppliersFromDropdown: CPI[],
    suppliers: SupplierResponseInterface[],
    selectedBlendId: string
  ) {
    if (selectedBlendId != '') {
      return [];
    }
    return suppliersFromDropdown.filter((supplier) => {
      if (
        !supplier.selected &&
        suppliers.find((supplierIn) =>
          supplierIn.suppliers.map((supp) => supp.id).includes(supplier.id)
        )?.quantity !== 0
      ) {
        return true;
      }
      return false;
    });
  }

  checkedSuppliers(
    suppliersFromDropdown: CPI[],
    suppliers: SupplierResponseInterface[]
  ) {
    return suppliersFromDropdown.filter((supplier) => {
      return (
        supplier.selected &&
        suppliers.find((supplierIn) =>
          supplierIn.suppliers.map((supp) => supp.id).includes(supplier.id)
        )?.quantity === 0
      );
    });
  }

  getQuantityFromCheckedSuppliers(
    suppliersFromDropdown: CPI[],
    suppliers: SupplierResponseInterface[]
  ) {
    let quantity = 0;
    const uncheckedSuppliers = suppliersFromDropdown.filter((supplier) => {
      if (
        !supplier.selected &&
        suppliers.find((supplierIn) =>
          supplierIn.suppliers.map((supp) => supp.id).includes(supplier.id)
        )?.quantity !== 0
      ) {
        quantity +=
          suppliers.find((supplierIn) =>
            supplierIn.suppliers.map((supp) => supp.id).includes(supplier.id)
          )?.quantity || 0;
        return true;
      }
      return false;
    });
    return quantity;
  }

  remove(uncheckedSuppliers: CPI[], singleSupplier: SupplierQuantity[]) {
    return singleSupplier.filter(
      (supplier) =>
        !uncheckedSuppliers.map((supp) => supp.id).includes(supplier.id)
    );
  }

  /**
   * Maps the allocation of completes to selected suppliers and supplier blends.
   *
   * 1 Filters the selected suppliers;
   * 2 Finds the default blends;
   * 3 Filters the selected suppliers in the blends;
   * 4 Distributes the completes among the suppliers:
   *   - for a blend, the value is calculated according to the percentile;
   *   - a supplier receives the calculated quota;
   * 6 Distributes the remaining completes among the suppliers
   */
  mapSuppliers(
    cpi: CPI[],
    completesRequired: number,
    isServiceBuyerLoggedIn: boolean = false,
    supplierBlendData: GetSupplierBlendInterface[] = [],
    selectedBlendId: string = '',
    isAugmentSurvey: boolean = false
  ): PutSupplierObjectInterface[] {
    let quantityToRedistribute = 0;
    const uncheckedSuppliers = this.uncheckedSuppliers(
      cpi,
      this._suppliers,
      selectedBlendId
    );
    quantityToRedistribute += this.getQuantityFromCheckedSuppliers(
      cpi,
      this._suppliers
    );

    let singleSupplier: SupplierQuantity[] = new Suppliers(
      this._suppliers
    ).listOfUniqueSuppliers();

    const totalSuppliersQuantity = this.sumOfSuppliersQuantity(this._suppliers);

    if (totalSuppliersQuantity < completesRequired) {
      quantityToRedistribute =
        quantityToRedistribute + (totalSuppliersQuantity - completesRequired);
    }

    const checkedSuppliers = this.checkedSuppliers(cpi, this._suppliers);

    const selectedBlend = this.getSelectedBlendInfo(
      supplierBlendData,
      selectedBlendId
    );
    const supplierInGroup: Array<number> = [];

    for (const supplier of this._suppliers) {
      if (supplier.group_name != null) {
        for (const supplierData of supplier.suppliers) {
          supplierInGroup.push(supplierData.id);
        }
      }
    }

    const selectedCPISuppliers = cpi.filter(
      (c) =>
        isAugmentSurvey ||
        (c.selected && c.isActive) ||
        supplierInGroup.includes(c.id) ||
        selectedBlend.id !== ''
    );

    //@ts-ignore
    if (selectedBlend.id === '') {
      singleSupplier = singleSupplier.filter((s) => {
        const foundC = cpi.find((c) => c.id === s.id);
        if (foundC && foundC.selected) {
          return true;
        }
        return false;
      });
    }

    singleSupplier = this.remove(uncheckedSuppliers, singleSupplier);
    const allocation = AllocationFactory.allocation(
      singleSupplier,
      completesRequired,
      selectedBlend,
      this.theresSupplierWithoutQuantity(checkedSuppliers),
      quantityToRedistribute
    );

    return this.suppliersWithQuantity(
      selectedCPISuppliers.map((c) => {
        const supplier = this._suppliers!.find(({ suppliers }) =>
          suppliers.map((supp) => supp.id).includes(c.id)
        );
        let completes_goal: number = 0;
        let current_target: number = 0;
        if (supplier && (c.selected || selectedBlend)) {
          supplier.quantity = allocation.quantity(c.id);
          completes_goal = new Flexibility(
            supplier.flexValue,
            supplier.quantity,
            completesRequired
          ).goal();
        }
        const supplierFieldsToFetch = [
          'bid_cpi',
          'completes_goal',
          'cpi',
          'current_target',
          'flexValue',
          'group_name',
          'quantity',
          'suppliers',
        ];
        if (isServiceBuyerLoggedIn) {
          supplierFieldsToFetch.push('scpi');
        }
        const pick: PutSupplierObjectInterface = supplierFieldsToFetch.reduce(
          (obj, key) => ({ ...obj, [key]: (supplier as any)[key] }),
          {} as PutSupplierObjectInterface
        );
        pick.group_name = pick.group_name ? pick.group_name : '';

        return {
          ...pick,
          quantity: pick.quantity,
          completes_goal,
          current_target,
        };
      })
    );
  }

  suppliersWithQuantity(suppliers: PutSupplierObjectInterface[]) {
    return suppliers.filter((supplier) => supplier.quantity > 0);
  }

  getSelectedBlendInfo(
    supplierBlendData: GetSupplierBlendInterface[] = [],
    selectedBlendId: string = ''
  ) {
    // If blend is not applicable, do not return anything
    if (selectedBlendId === 'NA') {
      return {
        suppliers: [],
        config_name: '',
        id: '',
        is_default: false,
        blend_flexibility: 100,
      };
    }
    const selectedBlend = supplierBlendData.find(
      (b) => b.id === selectedBlendId
    );
    // If the selected blend exists, return that blend
    if (selectedBlend) {
      return selectedBlend;
    }
    // or return the default one
    return {
      suppliers: [],
      config_name: '',
      id: '',
      is_default: false,
      blend_flexibility: 100,
    };
  }

  getRemaningQuotaValueForBlends(
    remaningCompletes: number,
    suppliersInBlendTotal: number
  ): [number, number] {
    let remaningValue: number = 0;
    if (remaningCompletes > 0 && suppliersInBlendTotal > 0) {
      remaningValue = Math.floor(remaningCompletes / suppliersInBlendTotal);
      remaningValue =
        remaningValue + (remaningCompletes % suppliersInBlendTotal);
    }
    remaningCompletes = remaningCompletes - remaningValue;
    return [remaningCompletes, remaningValue];
  }

  getRemaningQuotaValue(remaningCompletes: number = 0): [number, number] {
    let remaningValue: number = 0;
    if (remaningCompletes > 0) {
      remaningValue = 1;
      remaningCompletes--;
      return [remaningCompletes, remaningValue];
    }
    return [remaningCompletes, remaningValue];
  }
}
